import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import confetti from "canvas-confetti";
import { API, useApp } from "../context";

// Thin blue birthday banner. Two things only: it's Isaiah's 9th birthday, and
// premium is free for everyone until Sept 27. Shows for EVERYONE on all header
// pages; hidden only inside a game or a learning tool; never blocks content.

const isHiddenRoute = (path) => {
  if (path.startsWith("/arcade/") && path !== "/arcade/lobby") return true; // a game
  if (path.startsWith("/learn/") && path !== "/learn/academy/forest") return true; // a learning tool
  return false;
};

// Confetti rains from the banner on these "arrival" pages when the party's on.
const CONFETTI_ROUTES = new Set(["/", "/login", "/signup"]);

const fmt = (ms) => {
  if (ms <= 0) return "0m";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  return `${m}m ${sec}s`;
};

// Confetti that pours DOWN from the banner, across the full width, and lingers.
const fireBannerConfetti = () => {
  const colors = ["#0ea5e9", "#3b82f6", "#6366f1", "#ffffff", "#38bdf8", "#818cf8", "#fde047"];
  const shoot = (count) => {
    for (let i = 0; i <= 12; i++) {
      confetti({
        particleCount: count,
        startVelocity: 32,
        angle: 270, // downward, out of the banner
        spread: 120,
        gravity: 1.1,
        ticks: 320, // linger longer so it's clearly visible
        origin: { x: i / 12, y: 0.05 },
        colors,
        zIndex: 70,
        scalar: 1.15,
        disableForReducedMotion: true,
      });
    }
  };
  shoot(18);
  setTimeout(() => shoot(14), 300);
  setTimeout(() => shoot(12), 650);
};

export default function BirthdayLaunchBanner() {
  const { user } = useApp();
  const [win, setWin] = useState(null); // { active, ends_at, label }
  const [now, setNow] = useState(Date.now());
  const navigate = useNavigate();
  const location = useLocation();
  const confettiFiredRef = useRef(false);

  const poll = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/premium-preview/active`);
      // Birthday banner never shows for the fall promo (theme "fall" has its own banner).
      setWin(r.data?.active && r.data?.theme !== "fall" ? r.data : null);
    } catch (_) {
      setWin(null);
    }
  }, []);

  useEffect(() => {
    poll();
    const p = setInterval(poll, 60000);
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(p);
      clearInterval(t);
    };
  }, [poll]);

  const path = (location.pathname || "/").replace(/\/+$/, "") || "/";
  const onArrival = CONFETTI_ROUTES.has(path);
  const active = !!win?.active && !!win.ends_at && new Date(win.ends_at).getTime() > now;

  useEffect(() => {
    if (active && onArrival && !confettiFiredRef.current) {
      confettiFiredRef.current = true;
      fireBannerConfetti();
      const again = setInterval(fireBannerConfetti, 60000); // once a minute on the page
      return () => clearInterval(again);
    }
    if (!onArrival || !active) confettiFiredRef.current = false;
  }, [active, onArrival]);

  if (isHiddenRoute(path)) return null;
  if (!active) return null;
  const remaining = new Date(win.ends_at).getTime() - now;

  // Keep it: signed in → upgrade page; signed out → nothing (stay put).
  const onKeepIt = () => {
    if (user?.id) navigate("/subscribe");
  };

  const Balloons = () => (
    <span className="text-base sm:text-lg leading-none shrink-0 tracking-[-0.15em]" aria-hidden="true">
      <span className="inline-block animate-bounce">🎈</span>
      <span className="inline-block animate-bounce" style={{ animationDelay: "0.15s" }}>🎈</span>
      <span className="inline-block animate-bounce" style={{ animationDelay: "0.3s" }}>🎈</span>
    </span>
  );

  const MiniBalloons = () => (
    <span className="text-sm leading-none shrink-0 tracking-[-0.15em]" aria-hidden="true">
      <span className="inline-block animate-bounce">🎈</span>
      <span className="inline-block animate-bounce" style={{ animationDelay: "0.2s" }}>🎈</span>
    </span>
  );

  return (
    <div
      data-testid="birthday-launch-banner"
      className="w-full bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-600 text-white shadow overflow-hidden"
    >
      {/* Mobile: compact two-line layout, nothing clipped, nothing crammed.
          The whole bar is a gentle tap target to the upgrade page. */}
      <div
        role="button"
        tabIndex={0}
        onClick={onKeepIt}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onKeepIt(); }}
        data-testid="birthday-banner-mobile"
        className="lg:hidden px-3 py-1.5 flex flex-col items-center justify-center gap-0.5 text-center cursor-pointer active:opacity-90 transition-opacity"
      >
        <div className="flex items-center gap-1.5">
          <MiniBalloons />
          <span className="font-extrabold text-[12px] leading-none whitespace-nowrap">🎉 Happy 9th Birthday, Isaiah!</span>
          <MiniBalloons />
        </div>
        <div className="flex items-center justify-center gap-1.5 leading-none">
          <span className="text-[10px] font-bold uppercase tracking-wide text-yellow-300 whitespace-nowrap">Free Premium Access</span>
          <span className="text-sky-200/70 text-[10px]">•</span>
          <span
            data-testid="birthday-countdown-mobile"
            className="tabular-nums bg-white/20 rounded px-1.5 py-0.5 text-[10px] font-extrabold text-white whitespace-nowrap"
          >
            {fmt(remaining)} left
          </span>
        </div>
      </div>

      {/* Wide screens only: full single-row layout */}
      <div className="hidden lg:flex items-center justify-center gap-3 py-1.5 px-3 text-sm font-bold">
        <Balloons />
        <span className="flex flex-col items-center leading-none gap-0.5">
          <span className="font-extrabold whitespace-nowrap">🎉 Happy 9th Birthday, Isaiah!</span>
          <span className="text-[11px] font-bold text-sky-100 tracking-wide whitespace-nowrap">His birthday is September 26</span>
        </span>
        <span
          data-testid="birthday-free-chip"
          className="inline-block bg-yellow-300 text-blue-900 font-black uppercase tracking-wide text-xs rounded-full px-2.5 py-0.5 shadow-sm whitespace-nowrap shrink-0"
        >
          🎉 Free Premium Access
        </span>
        <span className="font-semibold text-sky-50 whitespace-nowrap hidden md:inline">
          All premium tools free until <span className="text-white font-extrabold">September 27</span>
        </span>
        <span
          data-testid="birthday-countdown"
          className="tabular-nums bg-white/20 rounded-md px-2 py-0.5 text-white font-extrabold whitespace-nowrap shrink-0"
        >
          {fmt(remaining)} left
        </span>
        <button
          type="button"
          onClick={onKeepIt}
          data-testid="birthday-keep-it"
          className="inline-flex items-center gap-0.5 bg-white/20 hover:bg-white/30 transition-colors rounded-full px-3 py-1 shrink-0"
        >
          Keep it <ChevronRight size={14} />
        </button>
        <Balloons />
      </div>
    </div>
  );
}
